import React, { useState, useEffect, useCallback } from 'react';
import DynamicBackground from '../../components/DynamicBackground';
import CreditsDisplay from '../../components/CreditsDisplay';
import ExpertContactMessage from '../../components/ExpertContactMessage';
import '../../styles/darkMode.css';
import './AnalysisDashboard.css';
import { getUserAnalyses, getAnalysisById, deleteAnalysis } from '../../services/analysis.service';
import { DocumentAnalysis } from '../../types/api.types';
import { useUser } from '../../contexts/UserContext';
import { parseResponse } from '../../services/contract-parser.service';
import { generatePDF } from '../../utils/pdfGenerator';

import ChatInterface from '../../components/ChatInterface';
import StarRating from '../../components/StarRating/StarRating';

const PAGE_SIZE = 10;

const AnalysisDashboard: React.FC = () => {
  const { user } = useUser();
  const [analyses, setAnalyses] = useState<DocumentAnalysis[]>([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState<DocumentAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'record-deal' | 'copyright-agreement' | 'booking-performance' | 'master-license' | 'media-contract'>('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const loadAnalyses = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: 0,
        ...(filterType !== 'all' && { analysisType: filterType })
      };
      
      const data = await getUserAnalyses(params);
      setAnalyses(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analyses');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, filterType]);

  const loadMore = useCallback(async () => {
    if (!user?.id || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: analyses.length,
        ...(filterType !== 'all' && { analysisType: filterType })
      };

      const data = await getUserAnalyses(params);
      setAnalyses(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more analyses');
    } finally {
      setIsLoadingMore(false);
    }
  }, [user?.id, analyses.length, filterType, isLoadingMore]);

  useEffect(() => {
    if (user?.id) {
      loadAnalyses();
    }
  }, [user?.id, loadAnalyses]);

  const handleAnalysisClick = async (analysis: DocumentAnalysis) => {
    try {
      const detailedAnalysis = await getAnalysisById(analysis.id);
      setSelectedAnalysis(detailedAnalysis);
      setShowDetailModal(true);
      setShowChat(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analysis details');
    }
  };



  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Prevent card click
    
    if (window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) {
      try {
        await deleteAnalysis(id);
        // Update state to remove the deleted item
        setAnalyses(prev => prev.filter(a => a.id !== id));
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to delete analysis');
      }
    }
  };

  const handleDownloadPDF = async () => {
    if (isGeneratingPDF || !selectedAnalysis) return;
    setIsGeneratingPDF(true);
    try {
      // Use the original filename but with .pdf extension (or appended)
      const baseName = selectedAnalysis.fileName.replace(/\.[^/.]+$/, "");
      const fileName = `${baseName}-analysis`;
      await generatePDF('analysis-detail-content', fileName);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const renderIssues = (issues: any[] | undefined, className: string) => {
    if (!issues || issues.length === 0) return null;
    
    const renderFormattedText = (text: string) => {
      if (typeof text !== 'string') return null;
      
      // Split the text by **bold** pattern
      const parts = text.split(/(\*\*.*?\*\*)/g);
      
      return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // Remove the stars and wrap in strong tag
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };
    
    return (
      <ul className={`issues-list ${className}`}>
        {issues.map((issue, index) => {
          if (typeof issue === 'string') {
            return (
              <li key={index}>
                {renderFormattedText(issue)}
              </li>
            );
          } else {
            // It's a structured Issue object
            return (
              <li key={index} className="structured-issue">
                <div className="issue-item">
                  <strong>{issue.item}</strong>
                </div>
                <div className="issue-explanation">
                  {renderFormattedText(issue.explanation)}
                </div>
                {(issue.Fix || issue.fix) && (
                  <div className="issue-fix">
                    <span className="fix-label">Suggested Fix:</span> {issue.Fix || issue.fix}
                  </div>
                )}
              </li>
            );
          }
        })}
      </ul>
    );
  };

  const renderTier1Fundamentals = (fundamentals: any[] | undefined) => {
    if (!fundamentals || fundamentals.length === 0) return null;

    return (
      <div className="analysis-section fundamentals-section">
        <h4>Tier-1 Fundamentals</h4>
        <div className="fundamentals-grid">
          {fundamentals.map((fundamental, index) => (
            <div key={index} className="fundamental-card">
              <div className="fundamental-heading">{fundamental.heading}</div>
              <div className="fundamental-observation">{fundamental.observation}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAnalysisDetail = (analysis: DocumentAnalysis) => {
    const parsedAnalysis = parseResponse(analysis.response);
    
    return (
      <div className="analysis-detail" id="analysis-detail-content">
        <div className="analysis-header">
          <h3>{analysis.fileName}</h3>
          <div className="analysis-meta">
            <span className={`analysis-type ${analysis.analysisType}`}>
              {getAnalysisTypeLabel(analysis.analysisType)}
            </span>
            <span className="analysis-date">
              {new Date(analysis.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {parsedAnalysis.isStructured ? (
          <>
            {parsedAnalysis.summary && (
              <div className="analysis-section summary-section">
                <h4>Summary</h4>
                <p>{parsedAnalysis.summary}</p>
              </div>
            )}

            {renderTier1Fundamentals(parsedAnalysis.tier1Fundamentals)}

            {parsedAnalysis.redIssues && parsedAnalysis.redIssues.length > 0 && (
              <div className="analysis-section red-section">
                <h4>Highly Problematic Issues</h4>
                {renderIssues(parsedAnalysis.redIssues, 'red-issues')}
              </div>
            )}

            {parsedAnalysis.yellowIssues && parsedAnalysis.yellowIssues.length > 0 && (
              <div className="analysis-section yellow-section">
                <h4>Potential Long-Term Problems</h4>
                {renderIssues(parsedAnalysis.yellowIssues, 'yellow-issues')}
              </div>
            )}

            {parsedAnalysis.greenIssues && parsedAnalysis.greenIssues.length > 0 && (
              <div className="analysis-section green-section">
                <h4>Negotiable, Non-Critical Points</h4>
                {renderIssues(parsedAnalysis.greenIssues, 'green-issues')}
              </div>
            )}

            {parsedAnalysis.overallEvaluation && (
              <div className="analysis-section evaluation-section">
                <h4>Overall Evaluation</h4>
                <p>{parsedAnalysis.overallEvaluation}</p>
              </div>
            )}

            {parsedAnalysis.remainingUncertainties && parsedAnalysis.remainingUncertainties.length > 0 && (
              <div className="analysis-section uncertainties-section">
                <h4>Remaining Uncertainties</h4>
                {renderIssues(parsedAnalysis.remainingUncertainties, 'uncertainties')}
              </div>
            )}
          </>
        ) : (
          <div className="analysis-section unstructured-section">
            <h4>Analysis Result</h4>
            <div className="unstructured-content">
              {parsedAnalysis.summary}
            </div>
          </div>
        )}
      </div>
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAnalysisTypeLabel = (type: string): string => {
    switch (type) {
      case 'record-deal':
        return 'Record Deal';
      case 'copyright-agreement':
        return 'Copyright Agreement';
      case 'booking-performance':
        return 'Booking / Performance Agreement';
      case 'master-license':
        return 'Master Licensing';
      case 'media-contract':
        return 'Media Contract';
      default:
        return type;
    }
  };

  return (
    <DynamicBackground>
      <div className="dark-container">
        <div className="dashboard-header">
          <CreditsDisplay variant="header" />
          <h2 className="dark-title">Contract Analysis History</h2>
          <p className="dark-subtitle">View and manage your past contract analyses</p>
        </div>

        <div className="filter-section">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="dark-select filter-dropdown"
          >
            <option value="all">All Analyses</option>
            <option value="record-deal">Record Deals</option>
            <option value="copyright-agreement">Copyright Agreements</option>
            <option value="booking-performance">Booking / Performance Agreements</option>
            <option value="master-license">Master Licensing</option>
            <option value="media-contract">Media Contracts</option>
          </select>
        </div>

        {isLoading ? (
          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p className="dark-text">Loading your analyses...</p>
          </div>
        ) : error ? (
          <div className="error-section">
            <p className="dark-text error-text">{error}</p>
            <button 
              className="dark-button primary"
              onClick={loadAnalyses}
            >
              Try Again
            </button>
          </div>
        ) : analyses.length === 0 ? (
          <div className="empty-section">
            <p className="dark-text">No analyses found. Start by analyzing a contract!</p>
          </div>
        ) : (
          <>
            <div className="analyses-grid">
              {analyses.map((analysis) => (
                <div 
                  key={analysis.id} 
                  className="analysis-card dark-card"
                  onClick={() => handleAnalysisClick(analysis)}
                >
                  <button 
                    className="delete-button"
                    onClick={(e) => handleDelete(e, analysis.id)}
                    title="Delete analysis"
                  >
                    ×
                  </button>
                  <div className="card-header">
                    <h3 className="dark-text">{analysis.fileName}</h3>
                    <span className={`analysis-type-badge ${analysis.analysisType}`}>
                      {getAnalysisTypeLabel(analysis.analysisType)}
                    </span>
                  </div>
                  <div className="card-meta">
                    <span className="file-size">{formatFileSize(analysis.fileSize)}</span>
                    <span className="analysis-date">
                      {new Date(analysis.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="card-preview">
                    {analysis.rating ? (
                      <div className="star-rating-badge">
                        {[1,2,3,4,5].map((s) => (
                          <span key={s} className="star-rating-badge-icon" style={{ color: s <= analysis.rating! ? 'var(--accent-primary)' : 'var(--surface-border)' }}>★</span>
                        ))}
                      </div>
                    ) : (
                      <p className="dark-text-muted">Click to view detailed analysis...</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <button
                  className="dark-button primary"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedAnalysis && (
          <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="dark-text">Analysis Details</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    className="dark-button primary"
                    onClick={handleDownloadPDF}
                    disabled={isGeneratingPDF}
                    style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                  >
                    {isGeneratingPDF ? (
                      <>
                        <span className="loading-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                        Generating...
                      </>
                    ) : (
                      'Download PDF'
                    )}
                  </button>
                  <button 
                    className="modal-close"
                    onClick={() => setShowDetailModal(false)}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="modal-body">
                {renderAnalysisDetail(selectedAnalysis)}
                {selectedAnalysis && (
                  !showChat ? (
                    <button
                      className="chat-cta-button"
                      onClick={() => setShowChat(true)}
                    >
                      <span className="chat-cta-icon">💬</span>
                      <span className="chat-cta-text">
                        <strong>Ask questions about this analysis</strong>
                        <small>Chat with AI about specific clauses, terms, or concerns</small>
                      </span>
                      <span className="chat-cta-arrow">›</span>
                    </button>
                  ) : (
                    <ChatInterface
                      context={selectedAnalysis.response}
                      title="Ask questions about this analysis"
                    />
                  )
                )}

                {selectedAnalysis && (
                  <StarRating
                    analysisId={selectedAnalysis.id}
                    existingRating={selectedAnalysis.rating}
                    existingComment={selectedAnalysis.ratingComment}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <ExpertContactMessage />
      </div>
    </DynamicBackground>
  );
};

export default AnalysisDashboard; 